import React, { useEffect, useState } from "react";
import { Line, Doughnut, Pie, PolarArea, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  PointElement,
} from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  PointElement
);

const LineChart = ({ totalClickCount, displayData = [] }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (displayData.length > 0) {
      setChartData({
        labels: displayData.map((item) => item.date),
        datasets: [
          {
            label: "Total Clicks",
            data: displayData.map((item) => item.totalClicks),
            borderColor: "#007bff",
            backgroundColor: "rgba(0, 123, 255, 0.2)",
            pointRadius: 5,
            tension: 0.4,
            fill: true,
          },
        ],
      });
    }
  }, [displayData]); // Depend on displayData to update when it changes

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Total Clicks Over Time: ${totalClickCount} Clicks`,
      },
    },
    scales: {
      x: { type: "category" },
      y: { beginAtZero: true },
    },
  };

  return (
    <div className="col-12" style={{ height: "400px", width: "100%" }}>
      {chartData ? (
        <Line data={chartData} options={options} />
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
};

const HalfMoonChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!data) return;
    // Extract rates and convert to numbers
    const successRate = parseFloat(data.successRate); // Example: "99.95%" -> 99.95
    const bounceRate = parseFloat(data.bounceRate);
    const complaintRate = parseFloat(data.complaintRate);
    const rejectRate = parseFloat(data.rejectRate);

    // Normalize for visibility (ensuring all values are visible)
    const minVisible = 2; // Minimum visible size in the chart
    let adjustedSuccessRate = successRate;
    let adjustments = 0;

    if (bounceRate > 0 && bounceRate < minVisible) {
      adjustments += minVisible - bounceRate;
    }
    if (complaintRate > 0 && complaintRate < minVisible) {
      adjustments += minVisible - complaintRate;
    }
    if (rejectRate > 0 && rejectRate < minVisible) {
      adjustments += minVisible - rejectRate;
    }

    // Reduce successRate proportionally to maintain 100%
    adjustedSuccessRate -= adjustments;

    setChartData({
      labels: ["Success", "Bounces", "Complaints", "Rejects"],
      datasets: [
        {
          data: [
            adjustedSuccessRate,
            Math.max(bounceRate, minVisible),
            Math.max(complaintRate, minVisible),
            Math.max(rejectRate, minVisible),
          ],
          backgroundColor: ["#28a745", "#ff9800", "#dc3545", "#6c757d"], // Green, Orange, Red, Gray
          borderWidth: 1,
        },
      ],
    });
  }, [data]);

  const options = {
    // rotation: -90, // Start from top
    // circumference: 180, // Show only half the chart
    cutout: "100%", // Make it a semi-doughnut
    plugins: {
      legend: { display: true, position: "bottom" },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const labels = [
              "Success Rate",
              "Bounce Rate",
              "Complaint Rate",
              "Reject Rate",
            ];
            const rates = [
              data.successRate,
              data.bounceRate,
              data.complaintRate,
              data.rejectRate,
            ];
            return `${labels[tooltipItem.dataIndex]}: ${
              rates[tooltipItem.dataIndex]
            }`;
          },
        },
      },
    },
  };

  return (
    <div className="col-4">
      <h4>Email Performance Report</h4>
      {chartData ? (
        <>
          <Doughnut data={chartData} options={options} height={100} />
        </>
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
  );
};

const FullMoonChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!data) return;

    // Extract rates and convert to numbers
    const successRate = parseFloat(data.successRate);
    const bounceRate = parseFloat(data.bounceRate);
    const complaintRate = parseFloat(data.complaintRate);
    const rejectRate = parseFloat(data.rejectRate);

    // Ensure small values are visible
    const minVisible = 2;
    let adjustedSuccessRate = successRate;
    let adjustments = 0;

    if (bounceRate > 0 && bounceRate < minVisible) {
      adjustments += minVisible - bounceRate;
    }
    if (complaintRate > 0 && complaintRate < minVisible) {
      adjustments += minVisible - complaintRate;
    }
    if (rejectRate > 0 && rejectRate < minVisible) {
      adjustments += minVisible - rejectRate;
    }

    // Reduce successRate proportionally to maintain 100%
    adjustedSuccessRate -= adjustments;

    setChartData({
      labels: ["Success", "Bounces", "Complaints", "Rejects"],
      datasets: [
        {
          data: [
            adjustedSuccessRate,
            Math.max(bounceRate, minVisible),
            Math.max(complaintRate, minVisible),
            Math.max(rejectRate, minVisible),
          ],
          backgroundColor: ["#28a745", "#ff9800", "#dc3545", "#6c757d"], // Green, Orange, Red, Gray
          borderWidth: 1,
        },
      ],
    });
  }, [data]);

  const options = {
    rotation: 0, // Start from top
    circumference: 360, // Full circle
    cutout: "50%", // Doughnut style
    plugins: {
      legend: { display: true, position: "bottom" },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const labels = [
              "Success Rate",
              "Bounce Rate",
              "Complaint Rate",
              "Reject Rate",
            ];
            const rates = [
              data.successRate,
              data.bounceRate,
              data.complaintRate,
              data.rejectRate,
            ];
            return `${labels[tooltipItem.dataIndex]}: ${
              rates[tooltipItem.dataIndex]
            }`;
          },
        },
      },
    },
  };

  return (
    // <div className="col-4">
    //   {/* <h4>Email Performance Report</h4> */}
    //   {chartData ? (
    //     <>
    //       <Doughnut data={chartData} options={options} />

    //     </>
    //   ) : (
    //     <p>Loading chart...</p>
    //   )}
    // </div>

    <div style={{ width: "300px", height: "300px", margin: "auto" }}>
      {chartData ? (
        <>
          <Doughnut data={chartData} options={options} />
        </>
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
    //  <>
    //   {chartData ? (
    //   <>
    //     <Doughnut data={chartData} options={options} />

    //   </>
    // ) : (
    //   <p>Loading chart...</p>
    // )}
    //  </>
  );
};

const PieChart = ({ eventData }) => {
  // State for the chart data and original values
  const [chartData, setChartData] = useState(null);
  const [originalValues, setOriginalValues] = useState([0, 0, 0, 0]);

  useEffect(() => {
    // Ensure eventCounts exist and is an array
    if (!eventData.eventCounts || eventData.eventCounts.length === 0) return;

    // Extract event counts safely
    const totalDelivery =
      eventData.eventCounts.find((event) => event.eventType === "Delivery")
        ?.count || 0;
    const totalBounces =
      eventData.eventCounts.find((event) => event.eventType === "Bounce")
        ?.count || 0;
    const totalComplaints =
      eventData.eventCounts.find((event) => event.eventType === "Complaint")
        ?.count || 0;
    const totalRejects =
      eventData.eventCounts.find((event) => event.eventType === "Reject")
        ?.count || 0;

    const total = totalDelivery + totalBounces + totalComplaints + totalRejects;
    if (total === 0) return;

    // Save original values
    const originalData = [
      totalDelivery,
      totalBounces,
      totalComplaints,
      totalRejects,
    ];
    setOriginalValues(originalData);

    // Convert counts to percentages
    let delivery = (totalDelivery / total) * 100;
    let bounces = (totalBounces / total) * 100;
    let complaints = (totalComplaints / total) * 100;
    let rejects = (totalRejects / total) * 100;

    // Ensure small values are visible
    const minVisible = 2;
    let adjustments = 0;

    if (bounces > 0 && bounces < minVisible) {
      adjustments += minVisible - bounces;
      bounces = minVisible;
    }
    if (complaints > 0 && complaints < minVisible) {
      adjustments += minVisible - complaints;
      complaints = minVisible;
    }
    if (rejects > 0 && rejects < minVisible) {
      adjustments += minVisible - rejects;
      rejects = minVisible;
    }

    // Adjust delivery count
    delivery = Math.max(0, delivery - adjustments);

    // Update state with new chart data
    setChartData({
      labels: ["Total Delivery", "Bounces", "Complaints", "Rejects"],
      datasets: [
        {
          data: [delivery, bounces, complaints, rejects],
          backgroundColor: ["#28a745", "#ff9800", "#dc3545", "#6c757d"], // Green, Orange, Red, Gray
          borderWidth: 1,
        },
      ],
    });
  }, [eventData.eventCounts]); // Only triggers when eventData.eventCounts changes

  const options = {
    plugins: {
      legend: { display: true, position: "bottom" },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const index = tooltipItem.dataIndex;
            return `${tooltipItem.label}: ${originalValues[index] || 0} emails`;
          },
        },
      },
    },
  };

  return (
    <div style={{ width: "300px", height: "300px", margin: "auto" }}>
      {chartData ? (
        <Pie data={chartData} options={options} />
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
  );
};

const PolarChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!data) return;
    const updatedData = {
      labels: ["Sent Emails", "Remaining Quota", "Max Send Rate"],
      datasets: [
        {
          data: [
            data?.SentLast24Hours,
            data?.Max24HourSend - data?.SentLast24Hours,
            data?.MaxSendRate,
          ],
          backgroundColor: ["#007bff", "#e0e0e0", "#ff9800"], // Blue, Gray, Orange
          borderWidth: 1,
        },
      ],
    };

    setChartData(updatedData);
  }, [data]);

  // 🎯 Chart options
  const options = {
    responsive: true, // Ensures the chart resizes properly
    maintainAspectRatio: false, // Allows flexible height and width
    plugins: {
      legend: {
        display: true,
        position: "bottom", // Moves labels to the bottom
      },
      tooltip: {
        enabled: true, // Enables hover tooltips
        callbacks: {
          label: (tooltipItem) => {
            const dataset = tooltipItem.dataset;
            const value = dataset.data[tooltipItem.dataIndex];
            return ` ${tooltipItem.label}: ${value}`;
          },
        },
      },
    },
    scales: {
      r: {
        // suggestedMax: data.Max24HourSend * 1.1,
        ticks: {
          display: true, // Hides scale values if not needed
          stepSize: Math.ceil(data.Max24HourSend / 6),
        },
        grid: {
          circular: true, // Keeps the grid circular
        },
      },
    },
  };

  return (
    // <>
    // {chartData ? <PolarArea data={chartData} options={options} /> : <p>Loading chart...</p>}
    // </>
    <div style={{ width: "300px", height: "300px", margin: "auto" }}>
      {chartData ? (
        <PolarArea data={chartData} options={options} />
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
  );
};

const GroupedBarChart = ({ displayData = [] }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!displayData.length) return;

    const uniqueDates = [...new Set(displayData.map((point) => point.date))];

    const formattedData = {
      labels: uniqueDates, // Get unique dates
      datasets: [
        {
          label: "Delivery",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) => point.date === date && point.eventType === "Delivery"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          backgroundColor: "#36A2EB",
        },
        {
          label: "Bounces",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) => point.date === date && point.eventType === "Bounce"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          backgroundColor: "#FF6384",
        },
        {
          label: "Complaints",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) =>
                  point.date === date && point.eventType === "Complaint"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          backgroundColor: "#FF9F40",
        },
        {
          label: "Rejects",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) => point.date === date && point.eventType === "Reject"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          backgroundColor: "#FFCE56",
        },
      ],
    };

    setChartData(formattedData);
  }, [displayData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
    },
    scales: {
      x: {
        stacked: false,
        barPercentage: 0.8, // Adjust bar width to make them wider
      },
      y: {
        beginAtZero: true,
        min: 0, // Ensure the y-axis starts from zero
        ticks: {
          stepSize: 1, // Ensure that each step is at least 1 for visibility
        },
      },
    },
  };

  return (
    <div className="col-12" style={{ height: "400px" }}>
      {chartData ? (
        <Bar data={chartData} options={options} />
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
  );
};

const GroupedLineChart = ({ displayData = [] }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!displayData.length) return;

    const uniqueDates = [...new Set(displayData.map((point) => point.date))];

    const formattedData = {
      labels: uniqueDates, // Get unique dates
      datasets: [
        {
          label: "Delivery",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) => point.date === date && point.eventType === "Delivery"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          borderColor: "#36A2EB",
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          fill: true, // Area under the line will be filled
          tension: 0.4, // Smooths the line
        },
        {
          label: "Bounces",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) => point.date === date && point.eventType === "Bounce"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          borderColor: "#FF6384",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: true,
          tension: 0.4,
        },
        {
          label: "Complaints",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) =>
                  point.date === date && point.eventType === "Complaint"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          borderColor: "#FF9F40",
          backgroundColor: "rgba(255, 159, 64, 0.2)",
          fill: true,
          tension: 0.4,
        },
        {
          label: "Rejects",
          data: uniqueDates.map((date) =>
            displayData
              .filter(
                (point) => point.date === date && point.eventType === "Reject"
              )
              .reduce((acc, curr) => acc + curr.totalCount, 0)
          ),
          borderColor: "#FFCE56",
          backgroundColor: "rgba(255, 206, 86, 0.2)",
          fill: true,
          tension: 0.4,
        },
      ],
    };

    setChartData(formattedData);
  }, [displayData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
    },
    scales: {
      x: {
        stacked: false,
      },
      y: {
        beginAtZero: true,
        min: 0, // Ensure the y-axis starts from zero
        ticks: {
          stepSize: 1, // Ensure that each step is at least 1 for visibility
        },
      },
    },
  };

  return (
    <div className="col-12" style={{ height: "400px" }}>
      {chartData ? (
        <Line data={chartData} options={options} />
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
  );
};

export {
  LineChart,
  HalfMoonChart,
  PieChart,
  FullMoonChart,
  PolarChart,
  GroupedBarChart,
  GroupedLineChart,
};
